import React, { useState, useEffect } from 'react';
import {
  Video,
  Calendar,
  Clock,
  Users,
  Plus,
  Play,
  FileText,
  CheckCircle2,
  ListOrdered,
  Sparkles,
  ArrowRight,
  Globe2,
  Copy,
  Check,
  Search,
  MessageSquare,
  XCircle,
  AlertCircle,
  Trash2,
  CheckSquare,
  Square,
  RefreshCw,
  X,
  Share2,
  Radio,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';
import { useCall } from '../../context/CallContext.tsx';
import { useRealtime } from '../../context/RealtimeContext.tsx';
import { Avatar } from '../common/Avatar.tsx';
import { Modal } from '../common/Modal.tsx';
import { User } from '../../../shared/types.ts';

interface MeetingAttendee {
  id: string;
  name: string;
  role: string;
  avatarUrl?: string;
  email?: string;
  isExternal?: boolean;
}

interface TranscriptTurn {
  speaker: string;
  role: string;
  timestamp: string;
  text: string;
}

interface ActionItem {
  taskTitle: string;
  assignee: string;
  dueDate: string;
}

interface Meeting {
  id: string;
  title: string;
  description?: string;
  clientName?: string;
  hostUserId: string;
  hostName: string;
  scheduledAt: string;
  durationMinutes: number;
  status: 'scheduled' | 'live' | 'completed' | 'cancelled';
  meetingUrl: string;
  attendees: MeetingAttendee[];
  recordingUrl?: string;
  rescheduledAt?: string;
  rescheduledBy?: string;
  rescheduleReason?: string;
  cancelledAt?: string;
  cancelledBy?: string;
  cancellationReason?: string;
  transcriptData?: {
    bilingualTranscript: TranscriptTurn[];
    englishSummary: string;
    keyPoints: string[];
    decisions: string[];
    actionItems: ActionItem[];
  };
}

const toDateTimeLocal = (isoString?: string) => {
  if (!isoString) return '';
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const MeetingsWorkspace: React.FC = () => {
  const { token, user } = useAuth();
  const { joinCall, startGroupCall } = useCall();
  const { subscribe } = useRealtime();

  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [teamMembers, setTeamMembers] = useState<User[]>([]);
  const [activeTab, setActiveTab] = useState<'upcoming' | 'past' | 'cancelled'>('upcoming');
  const [selectedMeetingId, setSelectedMeetingId] = useState<string | null>(null);
  const [copiedSummary, setCopiedSummary] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // New Meeting Modal
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newClient, setNewClient] = useState('');
  const [newDateTime, setNewDateTime] = useState('');
  const [newDuration, setNewDuration] = useState('45');
  const [newDesc, setNewDesc] = useState('');
  const [selectedAttendeeIds, setSelectedAttendeeIds] = useState<string[]>([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // External Guests (Outside Company)
  const [externalGuests, setExternalGuests] = useState<{ email: string; name?: string }[]>([]);
  const [newExternalEmail, setNewExternalEmail] = useState('');
  const [newExternalName, setNewExternalName] = useState('');

  // Reschedule Modal
  const [reschedulingMeeting, setReschedulingMeeting] = useState<Meeting | null>(null);
  const [rescheduleDateTime, setRescheduleDateTime] = useState('');
  const [rescheduleDuration, setRescheduleDuration] = useState('45');
  const [rescheduleTitle, setRescheduleTitle] = useState('');
  const [rescheduleDesc, setRescheduleDesc] = useState('');
  const [rescheduleAttendees, setRescheduleAttendees] = useState<string[]>([]);
  const [rescheduleMemberSearch, setRescheduleMemberSearch] = useState('');
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [isRescheduling, setIsRescheduling] = useState(false);

  // External Guests (Reschedule Modal)
  const [rescheduleExternalGuests, setRescheduleExternalGuests] = useState<{ email: string; name?: string }[]>([]);
  const [rescheduleNewExternalEmail, setRescheduleNewExternalEmail] = useState('');
  const [rescheduleNewExternalName, setRescheduleNewExternalName] = useState('');

  // Cancel Modal
  const [cancellingMeeting, setCancellingMeeting] = useState<Meeting | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);

  // Instant Meeting State
  const [instantMeetingModalOpen, setInstantMeetingModalOpen] = useState(false);
  const [instantMeeting, setInstantMeeting] = useState<any>(null);
  const [isCreatingInstant, setIsCreatingInstant] = useState(false);
  const [instantAttendeeIds, setInstantAttendeeIds] = useState<string[]>([]);
  const [isSavingInstantAttendees, setIsSavingInstantAttendees] = useState(false);
  const [copiedInstantLink, setCopiedInstantLink] = useState(false);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage(null);
    }, 4500);
  };

  // The reason the server gave for a failed request, so the person sees why instead of nothing happening
  const readError = async (res: Response, fallback: string) => {
    const body = await res.json().catch(() => ({}));
    return `${fallback} ${body.error || `(HTTP ${res.status})`}`;
  };

  // What happened with the invitation emails (guests outside the workspace are emailed)
  const emailNote = (emails?: { emailsQueued: number; externalEmailsQueued: number; smtpConfigured: boolean } | null) => {
    if (!emails || emails.emailsQueued === 0) return '';
    if (!emails.smtpConfigured) {
      return ' Emails were NOT sent: email (SMTP) is not set up on the server.';
    }
    return emails.externalEmailsQueued > 0
      ? ` Emailed ${emails.externalEmailsQueued} guest${emails.externalEmailsQueued === 1 ? '' : 's'} and the team.`
      : ' Team members were emailed too.';
  };

  const fetchMeetings = async () => {
    if (!token) return;
    try {
      const res = await fetch('/api/meetings', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const list: Meeting[] = data.meetings || [];
        setMeetings(list);

        // Auto-select first completed meeting if none selected
        if (!selectedMeetingId) {
          const firstPast = list.find((m) => m.status === 'completed');
          if (firstPast) setSelectedMeetingId(firstPast.id);
        }
      }
    } catch (err) {
      console.error('Failed to load meetings:', err);
    }
  };

  const fetchTeamMembers = async () => {
    if (!token) return;
    try {
      const res = await fetch('/api/team/members', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const members: User[] = data.members || [];
        setTeamMembers(members);
        // Pre-select current user and team members by default
        setSelectedAttendeeIds(members.map((m) => m.id));
      }
    } catch (err) {
      console.error('Failed to load team members:', err);
    }
  };

  useEffect(() => {
    fetchMeetings();
    fetchTeamMembers();
  }, [token]);

  // Real-time synchronization
  useEffect(() => {
    const unsubscribe = subscribe((event) => {
      if (
        event.type === 'meeting_created' ||
        event.type === 'meeting_updated' ||
        event.type === 'meeting_cancelled'
      ) {
        fetchMeetings();
      }
    });
    return unsubscribe;
  }, [subscribe]);

  const upcomingMeetings = meetings.filter((m) => m.status === 'scheduled' || m.status === 'live');
  const pastMeetings = meetings.filter((m) => m.status === 'completed');
  const cancelledMeetings = meetings.filter((m) => m.status === 'cancelled');

  const selectedMeeting = meetings.find((m) => m.id === selectedMeetingId) || pastMeetings[0];

  // Attendee toggle helpers
  const handleToggleAttendee = (id: string) => {
    setSelectedAttendeeIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSelectAllAttendees = () => {
    setSelectedAttendeeIds(teamMembers.map((m) => m.id));
  };

  const handleClearAttendees = () => {
    setSelectedAttendeeIds(user ? [user.id] : []);
  };

  const handleToggleRescheduleAttendee = (id: string) => {
    setRescheduleAttendees((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSelectAllRescheduleAttendees = () => {
    setRescheduleAttendees(teamMembers.map((m) => m.id));
  };

  const handleClearRescheduleAttendees = () => {
    setRescheduleAttendees(user ? [user.id] : []);
  };

  const handleAddExternalGuest = () => {
    const email = newExternalEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) return;
    if (externalGuests.some((g) => g.email === email)) return;
    setExternalGuests((prev) => [
      ...prev,
      { email, name: newExternalName.trim() || email.split('@')[0] },
    ]);
    setNewExternalEmail('');
    setNewExternalName('');
  };

  const handleRemoveExternalGuest = (email: string) => {
    setExternalGuests((prev) => prev.filter((g) => g.email !== email));
  };

  // What was written in the in-call chat of the selected past meeting (kept with the meeting, not in Messages)
  const [meetingChat, setMeetingChat] = useState<
    { id: string; content: string; createdAt: string; senderName: string }[] | null
  >(null);
  useEffect(() => {
    const chatMeetingId = selectedMeeting?.id;
    if (activeTab !== 'past' || !chatMeetingId || !token) return;
    let cancelled = false;
    setMeetingChat(null);
    fetch(`/api/meetings/${chatMeetingId}/chat-history`, {
      headers: { Authorization: `Bearer ${token}`, ...(user?.id ? { 'x-demo-user-id': user.id } : {}) },
    })
      .then((r) => (r.ok ? r.json() : { messages: [] }))
      .then((d) => {
        if (!cancelled) setMeetingChat(d.messages || []);
      })
      .catch(() => {
        if (!cancelled) setMeetingChat([]);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab, selectedMeeting?.id, token, user?.id]);

  // Every meeting gets its own chat channel so the in-call chat & file sharing works for it
  const getMeetingChannelId = async (meetingId: string): Promise<string | undefined> => {
    try {
      const res = await fetch(`/api/meetings/${meetingId}/chat-channel`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
      });
      if (res.ok) {
        const data = await res.json();
        return data.channelId as string;
      }
      console.warn('Could not open the meeting chat room:', res.status);
    } catch (err) {
      console.warn('Could not open the meeting chat room:', err);
    }
    return undefined;
  };

  const handleStartInstantMeeting = async () => {
    setIsCreatingInstant(true);
    try {
      const res = await fetch('/api/meetings/instant', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
        body: JSON.stringify({
          title: `${user?.fullName || 'Team'}'s Instant Room`,
          attendeeIds: user?.id ? [user.id] : [],
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setInstantMeeting(data.meeting);
        setInstantAttendeeIds(user?.id ? [user.id] : []);
        setInstantMeetingModalOpen(true);
        fetchMeetings();
      } else {
        startGroupCall({ id: `instant-${Date.now()}`, name: 'Instant Team Sync' });
      }
    } catch (err) {
      console.error('Failed to create instant meeting:', err);
      startGroupCall({ id: `instant-${Date.now()}`, name: 'Instant Team Sync' });
    } finally {
      setIsCreatingInstant(false);
    }
  };

  const handleCopyInstantLink = () => {
    if (!instantMeeting) return;
    const url = `${window.location.origin}/?meeting=${instantMeeting.id}`;
    navigator.clipboard.writeText(url);
    setCopiedInstantLink(true);
    setTimeout(() => setCopiedInstantLink(false), 2000);
  };

  const handleToggleInstantAttendee = (id: string) => {
    setInstantAttendeeIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleAddInstantAttendees = async () => {
    if (!instantMeeting || instantAttendeeIds.length === 0) return;
    setIsSavingInstantAttendees(true);
    try {
      const res = await fetch(`/api/meetings/${instantMeeting.id}/attendees`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
        body: JSON.stringify({
          attendeeIds: instantAttendeeIds,
        }),
      });
      if (res.ok) {
        showToast(`Invited ${instantAttendeeIds.length} team members to the instant room. They got a notification and a chat message.`);
        fetchMeetings();
      } else {
        showToast(await readError(res, 'Could not invite the team members.'));
      }
    } catch (err) {
      console.error('Failed to invite attendees to instant meeting:', err);
      showToast('Could not invite the team members: the server could not be reached.');
    } finally {
      setIsSavingInstantAttendees(false);
    }
  };

  const handleAddRescheduleExternalGuest = () => {
    const email = rescheduleNewExternalEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) return;
    if (rescheduleExternalGuests.some((g) => g.email === email)) return;
    setRescheduleExternalGuests((prev) => [
      ...prev,
      { email, name: rescheduleNewExternalName.trim() || email.split('@')[0] },
    ]);
    setRescheduleNewExternalEmail('');
    setRescheduleNewExternalName('');
  };

  const handleRemoveRescheduleExternalGuest = (email: string) => {
    setRescheduleExternalGuests((prev) => prev.filter((g) => g.email !== email));
  };

  // Open Schedule Modal with default time tomorrow 10:00 AM
  const handleOpenScheduleModal = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(11, 0, 0, 0);
    setNewDateTime(toDateTimeLocal(tomorrow.toISOString()));
    setSelectedAttendeeIds(teamMembers.map((m) => m.id));
    setExternalGuests([]);
    setNewExternalEmail('');
    setNewExternalName('');
    setIsScheduleModalOpen(true);
  };

  const handleScheduleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle.trim() || !newDateTime || !token) return;
    setIsSubmitting(true);

    const attendeesPayload = teamMembers
      .filter((m) => selectedAttendeeIds.includes(m.id))
      .map((m) => ({
        id: m.id,
        name: m.fullName,
        role: m.title || m.role,
        avatarUrl: m.avatarUrl,
        email: m.email,
        isExternal: false,
      }));

    const externalAttendeesPayload = externalGuests.map((g, index) => ({
      id: `ext-${Date.now()}-${index}`,
      name: g.name || g.email,
      role: 'External Guest',
      email: g.email,
      isExternal: true,
    }));

    const allAttendees = [...attendeesPayload, ...externalAttendeesPayload];

    try {
      const res = await fetch('/api/meetings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          title: newTitle.trim(),
          clientName: newClient.trim() || 'IHB Pulse Operations',
          scheduledAt: new Date(newDateTime).toISOString(),
          durationMinutes: parseInt(newDuration, 10) || 45,
          description: newDesc.trim(),
          attendees: allAttendees,
        }),
      });

      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        setIsScheduleModalOpen(false);
        setNewTitle('');
        setNewClient('');
        setNewDesc('');
        setExternalGuests([]);
        setNewExternalEmail('');
        setNewExternalName('');
        fetchMeetings();
        showToast(`Meeting scheduled. Members got a notification and a chat message.${emailNote(data.emails)}`);
      } else {
        showToast(await readError(res, 'Could not schedule the meeting.'));
      }
    } catch (err) {
      console.error('Failed to schedule meeting:', err);
      showToast('Could not schedule the meeting: the server could not be reached.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open Reschedule Modal
  const handleOpenReschedule = (meeting: Meeting) => {
    setReschedulingMeeting(meeting);
    setRescheduleTitle(meeting.title);
    setRescheduleDesc(meeting.description || '');
    setRescheduleDateTime(toDateTimeLocal(meeting.scheduledAt));
    setRescheduleDuration(String(meeting.durationMinutes || 45));
    setRescheduleAttendees(
      meeting.attendees && meeting.attendees.length > 0
        ? meeting.attendees
            .filter((a) => !a.isExternal && !a.id?.startsWith('ext-'))
            .map((a) => a.id)
        : teamMembers.map((m) => m.id)
    );
    const existingExternals = (meeting.attendees || [])
      .filter((a) => a.isExternal || a.id?.startsWith('ext-'))
      .map((a) => ({ email: a.email || a.name, name: a.name }));
    setRescheduleExternalGuests(existingExternals);
    setRescheduleNewExternalEmail('');
    setRescheduleNewExternalName('');
    setRescheduleReason('');
  };

  const handleRescheduleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reschedulingMeeting || !rescheduleDateTime || !token) return;
    setIsRescheduling(true);

    const attendeesPayload = teamMembers
      .filter((m) => rescheduleAttendees.includes(m.id))
      .map((m) => ({
        id: m.id,
        name: m.fullName,
        role: m.title || m.role,
        avatarUrl: m.avatarUrl,
        email: m.email,
        isExternal: false,
      }));

    const externalAttendeesPayload = rescheduleExternalGuests.map((g, index) => ({
      id: `ext-${Date.now()}-${index}`,
      name: g.name || g.email,
      role: 'External Guest',
      email: g.email,
      isExternal: true,
    }));

    const allAttendees = [...attendeesPayload, ...externalAttendeesPayload];

    try {
      const res = await fetch(`/api/meetings/${reschedulingMeeting.id}/reschedule`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          title: rescheduleTitle.trim(),
          description: rescheduleDesc.trim(),
          scheduledAt: new Date(rescheduleDateTime).toISOString(),
          durationMinutes: parseInt(rescheduleDuration, 10) || 45,
          attendees: allAttendees,
          reason: rescheduleReason.trim(),
        }),
      });

      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        setReschedulingMeeting(null);
        fetchMeetings();
        showToast(`Meeting rescheduled. Everyone was notified.${emailNote(data.emails)}`);
      } else {
        showToast(await readError(res, 'Could not reschedule the meeting.'));
      }
    } catch (err) {
      console.error('Failed to reschedule meeting:', err);
      showToast('Could not reschedule the meeting: the server could not be reached.');
    } finally {
      setIsRescheduling(false);
    }
  };

  // Open Cancel Modal
  const handleOpenCancel = (meeting: Meeting) => {
    setCancellingMeeting(meeting);
    setCancelReason('');
  };

  const handleCancelSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cancellingMeeting || !token) return;
    setIsCancelling(true);

    try {
      const res = await fetch(`/api/meetings/${cancellingMeeting.id}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          reason: cancelReason.trim(),
        }),
      });

      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        setCancellingMeeting(null);
        fetchMeetings();
        showToast(`Meeting cancelled. Everyone was notified.${emailNote(data.emails)}`);
      } else {
        showToast(await readError(res, 'Could not cancel the meeting.'));
      }
    } catch (err) {
      console.error('Failed to cancel meeting:', err);
      showToast('Could not cancel the meeting: the server could not be reached.');
    } finally {
      setIsCancelling(false);
    }
  };

  // Delete Meeting Permanently
  const handleDeleteMeeting = async (meetingId: string) => {
    if (!token) return;
    if (!window.confirm('Are you sure you want to permanently delete this meeting?')) return;
    try {
      const res = await fetch(`/api/meetings/${meetingId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        fetchMeetings();
        showToast('Meeting deleted.');
      } else {
        showToast(await readError(res, 'Could not delete the meeting.'));
      }
    } catch (err) {
      console.error('Failed to delete meeting:', err);
      showToast('Could not delete the meeting: the server could not be reached.');
    }
  };

  const handleCopySummary = () => {
    if (!selectedMeeting?.transcriptData?.englishSummary) return;
    navigator.clipboard.writeText(selectedMeeting.transcriptData.englishSummary);
    setCopiedSummary(true);
    setTimeout(() => setCopiedSummary(false), 2000);
  };

  // Filtered members for modals
  const filteredScheduleMembers = teamMembers.filter((m) =>
    memberSearch.trim()
      ? m.fullName.toLowerCase().includes(memberSearch.toLowerCase()) ||
        (m.title && m.title.toLowerCase().includes(memberSearch.toLowerCase())) ||
        m.role.toLowerCase().includes(memberSearch.toLowerCase())
      : true
  );

  const filteredRescheduleMembers = teamMembers.filter((m) =>
    rescheduleMemberSearch.trim()
      ? m.fullName.toLowerCase().includes(rescheduleMemberSearch.toLowerCase()) ||
        (m.title && m.title.toLowerCase().includes(rescheduleMemberSearch.toLowerCase())) ||
        m.role.toLowerCase().includes(rescheduleMemberSearch.toLowerCase())
      : true
  );

  return (
    <div className="space-y-6 pb-12 animate-fadeIn relative">
      {/* Toast Alert Notification */}
      {toastMessage && (
        <div className="fixed top-20 right-6 z-50 flex items-center space-x-2.5 px-4 py-3 bg-surface-900 text-white dark:bg-white dark:text-black rounded-2xl shadow-elevated border border-brand-500/30 animate-fadeIn">
          <CheckCircle2 className="w-4 h-4 text-brand-400 dark:text-brand-600 shrink-0" />
          <span className="text-xs font-bold">{toastMessage}</span>
          <button
            onClick={() => setToastMessage(null)}
            className="ml-2 text-surface-400 hover:text-white dark:hover:text-black"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* 1. Compact Header Banner */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-3.5 py-2 bg-white dark:bg-[#121417] rounded-2xl border border-[#d5d8de]/60 dark:border-surface-800 shadow-2xs">
        <div className="flex items-center space-x-2">
          <div className="w-7 h-7 rounded-xl bg-gradient-to-tr from-[#0162fc] to-[#4b2efa] text-white flex items-center justify-center shadow-2xs shrink-0">
            <Video className="w-3.5 h-3.5 stroke-[2.5]" />
          </div>
          <h2 className="text-sm sm:text-base font-extrabold text-[#121417] dark:text-white uppercase tracking-tight">
            Meetings & AI Transcriptions
          </h2>
        </div>

        <div className="flex flex-wrap sm:flex-nowrap items-center gap-1.5 sm:gap-2 w-full sm:w-auto">
          <button
            onClick={handleStartInstantMeeting}
            disabled={isCreatingInstant}
            className="flex-1 sm:flex-none justify-center px-3 py-1.5 rounded-full border border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800 hover:bg-surface-100 text-[11px] font-bold text-surface-900 dark:text-white transition-all flex items-center space-x-1.5 disabled:opacity-50"
          >
            <Play className="w-3 h-3 text-brand-500 fill-brand-500" />
            <span>{isCreatingInstant ? 'Creating Room...' : 'Instant Room'}</span>
          </button>

          <button
            onClick={handleOpenScheduleModal}
            className="flex-1 sm:flex-none justify-center px-3 py-1.5 rounded-full bg-gradient-to-r from-[#0162fc] to-[#4b2efa] text-white text-[11px] font-bold shadow-pulse transition-transform hover:scale-105 flex items-center space-x-1"
          >
            <Plus className="w-3.5 h-3.5 text-white stroke-[3]" />
            <span>Schedule Meeting</span>
          </button>
        </div>
      </div>

      {/* 2. Mode Selector Pill Tabs */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-surface-200 dark:border-surface-800 pb-2 gap-2">
        <div className="flex items-center space-x-2 overflow-x-auto touch-scroll no-scrollbar py-1 w-full sm:w-auto -mx-1 px-1">
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`px-3.5 sm:px-4 py-2 min-h-[36px] shrink-0 rounded-full text-xs font-extrabold transition-all flex items-center space-x-2 ${
              activeTab === 'upcoming'
                ? 'bg-[#121417] text-white dark:bg-white dark:text-black shadow-sm'
                : 'text-surface-600 dark:text-surface-400 hover:bg-surface-200 dark:hover:bg-surface-800'
            }`}
          >
            <Calendar className="w-3.5 h-3.5" />
            <span>Upcoming ({upcomingMeetings.length})</span>
          </button>

          <button
            onClick={() => setActiveTab('past')}
            className={`px-3.5 sm:px-4 py-2 min-h-[36px] shrink-0 rounded-full text-xs font-extrabold transition-all flex items-center space-x-2 ${
              activeTab === 'past'
                ? 'bg-[#121417] text-white dark:bg-white dark:text-black shadow-sm'
                : 'text-surface-600 dark:text-surface-400 hover:bg-surface-200 dark:hover:bg-surface-800'
            }`}
          >
            <Sparkles className="w-3.5 h-3.5 text-brand-500" />
            <span>Past & Summaries ({pastMeetings.length})</span>
          </button>

          <button
            onClick={() => setActiveTab('cancelled')}
            className={`px-3.5 sm:px-4 py-2 min-h-[36px] shrink-0 rounded-full text-xs font-extrabold transition-all flex items-center space-x-2 ${
              activeTab === 'cancelled'
                ? 'bg-[#121417] text-white dark:bg-white dark:text-black shadow-sm'
                : 'text-surface-600 dark:text-surface-400 hover:bg-surface-200 dark:hover:bg-surface-800'
            }`}
          >
            <XCircle className="w-3.5 h-3.5 text-rose-500" />
            <span>Cancelled ({cancelledMeetings.length})</span>
          </button>
        </div>

        <span className="text-[11px] font-semibold text-surface-400 hidden sm:inline">
          Bilingual Urdu + English Code-Switching AI
        </span>
      </div>

      {/* 3. Tab Content: Upcoming Meetings */}
      {activeTab === 'upcoming' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {upcomingMeetings.map((meeting) => (
            <div
              key={meeting.id}
              className="p-5 rounded-3xl bg-white dark:bg-[#181b20] border border-[#d5d8de]/60 dark:border-surface-800 shadow-neo-card hover:shadow-neo transition-all flex flex-col justify-between space-y-4"
            >
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-[10px] font-mono font-bold px-2.5 py-0.5 rounded-full bg-brand-50 dark:bg-brand-950/60 text-brand-600 dark:text-brand-300 border border-brand-200 dark:border-brand-800">
                      {meeting.clientName || 'Client Project'}
                    </span>
                    {meeting.rescheduledBy && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-300/40">
                        Rescheduled
                      </span>
                    )}
                  </div>
                  <span className="text-xs font-semibold text-surface-400 flex items-center">
                    <Clock className="w-3.5 h-3.5 mr-1" />
                    {meeting.durationMinutes} mins
                  </span>
                </div>

                <h3 className="font-extrabold text-sm text-[#121417] dark:text-white leading-snug">
                  {meeting.title}
                </h3>
                {meeting.description && (
                  <p className="text-xs text-[#667085] dark:text-surface-400 mt-1 line-clamp-2">
                    {meeting.description}
                  </p>
                )}

                <div className="mt-3 flex items-center space-x-2 text-xs text-surface-600 dark:text-surface-300">
                  <Calendar className="w-3.5 h-3.5 text-brand-500" />
                  <span className="font-semibold">
                    {new Date(meeting.scheduledAt).toLocaleDateString([], {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                    })}{' '}
                    at{' '}
                    {new Date(meeting.scheduledAt).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>

                {meeting.rescheduleReason && (
                  <div className="mt-2 text-[11px] bg-amber-50/70 dark:bg-amber-950/30 text-amber-800 dark:text-amber-200 px-2.5 py-1 rounded-xl border border-amber-200/50">
                    <span className="font-bold">Rescheduled:</span> {meeting.rescheduleReason}
                  </div>
                )}
              </div>

              {/* Bottom: Attendees Stack + Action Buttons */}
              <div className="pt-3 border-t border-surface-100 dark:border-surface-800 flex flex-wrap items-center justify-between gap-2">
                {/* Attendees avatars & count */}
                <div
                  className="flex items-center space-x-2 cursor-pointer group"
                  title={meeting.attendees?.map((a) => `${a.name} (${a.role || (a.isExternal ? 'External Guest' : 'Member')})`).join(', ')}
                >
                  <div className="flex -space-x-2 overflow-hidden">
                    {meeting.attendees?.slice(0, 4).map((att) => (
                      <Avatar
                        key={att.id}
                        name={att.name}
                        size="xs"
                        className={`border-2 ${att.isExternal || att.id?.startsWith('ext-') ? 'ring-1 ring-amber-400 border-amber-400' : 'border-white dark:border-surface-900'}`}
                      />
                    ))}
                    {(meeting.attendees?.length || 0) > 4 && (
                      <div className="w-6 h-6 rounded-full bg-surface-200 dark:bg-surface-700 text-[10px] font-bold flex items-center justify-center text-surface-600 dark:text-surface-300 border-2 border-white dark:border-surface-900">
                        +{meeting.attendees.length - 4}
                      </div>
                    )}
                  </div>
                  <span className="text-xs text-surface-500 font-semibold group-hover:text-brand-600 transition-colors flex items-center">
                    <span>{meeting.attendees?.length || 0} attendees</span>
                    {meeting.attendees?.some((a) => a.isExternal || a.id?.startsWith('ext-')) && (
                      <span className="ml-1.5 px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700 dark:text-amber-300 font-bold text-[9px] border border-amber-500/30">
                        +Guest
                      </span>
                    )}
                  </span>
                </div>

                {/* Reschedule, Cancel & Join Call Actions */}
                <div className="flex items-center space-x-1.5">
                  <button
                    onClick={() => handleOpenReschedule(meeting)}
                    className="px-2.5 py-1.5 rounded-xl border border-surface-200 dark:border-surface-700 hover:bg-surface-100 dark:hover:bg-surface-800 text-xs font-bold text-surface-700 dark:text-surface-200 transition-colors flex items-center space-x-1"
                    title="Reschedule this session"
                  >
                    <Calendar className="w-3.5 h-3.5 text-brand-500" />
                    <span className="hidden sm:inline">Reschedule</span>
                  </button>

                  <button
                    onClick={() => handleOpenCancel(meeting)}
                    className="px-2.5 py-1.5 rounded-xl border border-rose-200 dark:border-rose-900/60 hover:bg-rose-50 dark:hover:bg-rose-950/40 text-xs font-bold text-rose-600 dark:text-rose-400 transition-colors flex items-center space-x-1"
                    title="Cancel meeting and notify participants"
                  >
                    <XCircle className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Cancel</span>
                  </button>

                  <button
                    onClick={async () =>
                      joinCall({
                        title: meeting.title,
                        type: 'group',
                        channelId: await getMeetingChannelId(meeting.id),
                      })
                    }
                    className="px-3.5 py-1.5 rounded-full bg-gradient-to-r from-[#0162fc] to-[#4b2efa] text-white text-xs font-bold shadow-pulse transition-transform hover:scale-105 flex items-center space-x-1.5"
                  >
                    <Video className="w-3.5 h-3.5 text-white fill-current" />
                    <span>Join Call</span>
                  </button>
                </div>
              </div>
            </div>
          ))}

          {upcomingMeetings.length === 0 && (
            <div className="col-span-2 p-12 text-center bg-white dark:bg-[#181b20] rounded-3xl border border-surface-200 dark:border-surface-800 text-surface-400 text-xs">
              No upcoming meetings scheduled. Click "Schedule Meeting" to create one.
            </div>
          )}
        </div>
      )}

      {/* 4. Tab Content: Cancelled Meetings */}
      {activeTab === 'cancelled' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {cancelledMeetings.map((meeting) => (
            <div
              key={meeting.id}
              className="p-5 rounded-3xl bg-white dark:bg-[#181b20] border border-rose-200/60 dark:border-rose-900/40 shadow-sm opacity-90 flex flex-col justify-between space-y-4"
            >
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-900/50 flex items-center space-x-1">
                    <XCircle className="w-3 h-3 mr-1" />
                    Cancelled {meeting.cancelledBy ? `by ${meeting.cancelledBy}` : ''}
                  </span>
                  <span className="text-xs font-semibold text-surface-400 flex items-center">
                    <Clock className="w-3.5 h-3.5 mr-1" />
                    {meeting.durationMinutes} mins
                  </span>
                </div>

                <h3 className="font-bold text-sm text-surface-500 dark:text-surface-400 line-through leading-snug">
                  {meeting.title}
                </h3>

                <div className="mt-2 text-xs text-rose-600 dark:text-rose-400 font-medium bg-rose-50/60 dark:bg-rose-950/20 p-2 rounded-xl">
                  <span className="font-bold">Reason:</span> {meeting.cancellationReason || 'No reason provided'}
                </div>

                <div className="mt-3 flex items-center space-x-2 text-xs text-surface-400">
                  <Calendar className="w-3.5 h-3.5" />
                  <span>
                    Originally was {new Date(meeting.scheduledAt).toLocaleDateString()} at{' '}
                    {new Date(meeting.scheduledAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>

              {/* Actions for Cancelled Meeting: Reschedule or Remove */}
              <div className="pt-3 border-t border-surface-100 dark:border-surface-800 flex items-center justify-between">
                <span className="text-xs text-surface-400 font-medium">
                  {meeting.attendees?.length || 0} participants
                </span>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleDeleteMeeting(meeting.id)}
                    className="p-1.5 text-surface-400 hover:text-rose-600 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
                    title="Delete permanently"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>

                  <button
                    onClick={() => handleOpenReschedule(meeting)}
                    className="px-3 py-1.5 rounded-xl bg-brand-500 hover:bg-brand-600 text-white text-xs font-bold shadow-sm transition-transform hover:scale-105 flex items-center space-x-1"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    <span>Reschedule / Restore</span>
                  </button>
                </div>
              </div>
            </div>
          ))}

          {cancelledMeetings.length === 0 && (
            <div className="col-span-2 p-12 text-center bg-white dark:bg-[#181b20] rounded-3xl border border-surface-200 dark:border-surface-800 text-surface-400 text-xs">
              No cancelled meetings.
            </div>
          )}
        </div>
      )}

      {/* 5. Tab Content: Past Meetings & AI Bilingual Transcription */}
      {activeTab === 'past' && selectedMeeting && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Past Meetings Selector */}
          <div className="space-y-3">
            <span className="text-xs font-extrabold uppercase tracking-wider text-surface-400 block px-1">
              Select Completed Session
            </span>
            <div className="flex lg:block overflow-x-auto touch-scroll no-scrollbar space-x-2 lg:space-x-0 lg:space-y-2 pb-2 lg:pb-0 -mx-1 px-1">
              {pastMeetings.map((m) => {
                const isSelected = m.id === selectedMeeting.id;
                return (
                  <button
                    key={m.id}
                    onClick={() => setSelectedMeetingId(m.id)}
                    className={`min-w-[240px] lg:min-w-0 w-full text-left p-3.5 sm:p-4 rounded-2xl border shrink-0 transition-all ${
                      isSelected
                        ? 'bg-white dark:bg-[#181b20] border-brand-500 shadow-neo ring-1 ring-brand-500/20'
                        : 'bg-surface-50 dark:bg-surface-900 border-surface-200 dark:border-surface-800 hover:bg-white dark:hover:bg-surface-850'
                    }`}
                  >
                    <div className="flex items-center justify-between text-[11px] mb-1">
                      <span className="font-mono font-bold text-brand-600 dark:text-brand-400">
                        {m.clientName || 'Project'}
                      </span>
                      <span className="text-surface-400">
                        {new Date(m.scheduledAt).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                    <div className="font-extrabold text-xs text-surface-900 dark:text-surface-100 line-clamp-1">
                      {m.title}
                    </div>
                    <div className="text-[10px] text-surface-500 mt-1 flex items-center space-x-2">
                      <span>{m.durationMinutes} mins</span>
                      <span>•</span>
                      <span>{m.attendees?.length || 4} participants</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right 2 Columns: Full AI Transcription & English Executive Summary Dossier */}
          <div className="lg:col-span-2 space-y-4">
            {/* Header info bar */}
            <div className="p-4 rounded-2xl bg-white dark:bg-[#181b20] border border-[#d5d8de]/60 dark:border-surface-800 shadow-sm flex items-center justify-between">
              <div>
                <h3 className="font-extrabold text-sm text-[#121417] dark:text-white">
                  {selectedMeeting.title}
                </h3>
                <span className="text-xs text-surface-400">
                  Conducted by {selectedMeeting.hostName} • {new Date(selectedMeeting.scheduledAt).toLocaleDateString()}
                </span>
              </div>
              <button
                onClick={handleCopySummary}
                className="px-3 py-1.5 rounded-full border border-surface-200 dark:border-surface-700 text-xs font-bold text-surface-700 dark:text-surface-300 hover:bg-surface-100 flex items-center space-x-1"
                title="Copy English Executive Summary"
              >
                {copiedSummary ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                <span>{copiedSummary ? 'Copied' : 'Copy Summary'}</span>
              </button>
            </div>

            {/* AI Executive Summary Card */}
            <div className="p-6 rounded-3xl bg-white dark:bg-[#181b20] border border-[#d5d8de]/60 dark:border-surface-800 shadow-neo space-y-4">
              <div className="flex items-center space-x-2 text-xs font-extrabold text-brand-600 dark:text-brand-400 uppercase tracking-wider">
                <Sparkles className="w-4 h-4 text-brand-500" />
                <span>AI Executive Summary (Translated & Synthesized in English)</span>
              </div>

              <p className="text-xs text-surface-800 dark:text-surface-200 leading-relaxed font-medium">
                {selectedMeeting.transcriptData?.englishSummary}
              </p>

              {/* Key Points & Decisions */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                <div className="p-3.5 rounded-2xl bg-surface-50 dark:bg-surface-900 border border-surface-200/60 dark:border-surface-800 space-y-2">
                  <span className="text-[11px] font-bold text-surface-900 dark:text-surface-100 uppercase tracking-wider block">
                    Key Discussion Points:
                  </span>
                  <ul className="space-y-1.5 text-xs text-surface-600 dark:text-surface-300">
                    {selectedMeeting.transcriptData?.keyPoints.map((pt, i) => (
                      <li key={i} className="flex items-start space-x-2">
                        <span className="text-brand-500 font-bold">•</span>
                        <span>{pt}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="p-3.5 rounded-2xl bg-surface-50 dark:bg-surface-900 border border-surface-200/60 dark:border-surface-800 space-y-2">
                  <span className="text-[11px] font-bold text-surface-900 dark:text-surface-100 uppercase tracking-wider block">
                    Decisions Made:
                  </span>
                  <ul className="space-y-1.5 text-xs text-surface-600 dark:text-surface-300">
                    {selectedMeeting.transcriptData?.decisions.map((dec, i) => (
                      <li key={i} className="flex items-start space-x-2">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                        <span>{dec}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Bilingual Urdu + English Audio/Dialogue Transcript */}
            <div className="p-6 rounded-3xl bg-white dark:bg-[#181b20] border border-[#d5d8de]/60 dark:border-surface-800 shadow-neo space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 text-xs font-extrabold text-surface-900 dark:text-surface-100 uppercase tracking-wider">
                  <Globe2 className="w-4 h-4 text-brand-500" />
                  <span>Bilingual Raw Transcript (Urdu & English Speech Recognition)</span>
                </div>
                <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-surface-100 dark:bg-surface-800 text-surface-500">
                  Dual Audio Channel • 42 mins
                </span>
              </div>

              <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                {selectedMeeting.transcriptData?.bilingualTranscript.map((turn, i) => (
                  <div
                    key={i}
                    className="p-3.5 rounded-2xl bg-surface-50/70 dark:bg-surface-900/60 border border-surface-200/40 dark:border-surface-800 flex space-x-3 text-xs"
                  >
                    <div className="shrink-0">
                      <Avatar name={turn.speaker} size="sm" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center space-x-1.5">
                          <span className="font-extrabold text-surface-900 dark:text-surface-100">
                            {turn.speaker}
                          </span>
                          <span className="text-[10px] text-surface-400">({turn.role})</span>
                        </div>
                        <span className="text-[10px] font-mono text-surface-400">{turn.timestamp}</span>
                      </div>
                      <p className="text-surface-700 dark:text-surface-300 leading-relaxed font-sans">
                        {turn.text}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* In-call chat of this meeting (kept with the meeting, like Google Meet) */}
            <div className="p-6 rounded-3xl bg-white dark:bg-[#181b20] border border-[#d5d8de]/60 dark:border-surface-800 shadow-neo space-y-3">
              <div className="flex items-center space-x-2 text-xs font-extrabold text-surface-900 dark:text-surface-100 uppercase tracking-wider">
                <MessageSquare className="w-4 h-4 text-brand-500" />
                <span>Meeting Chat</span>
              </div>
              {meetingChat === null ? (
                <p className="text-xs text-surface-400">Loading chat...</p>
              ) : meetingChat.length === 0 ? (
                <p className="text-xs text-surface-400">Nobody wrote in the chat during this meeting.</p>
              ) : (
                <div className="space-y-2 max-h-72 overflow-y-auto pr-2">
                  {meetingChat.map((m) => (
                    <div key={m.id} className="text-xs">
                      <span className="font-extrabold text-surface-900 dark:text-surface-100">{m.senderName}</span>
                      <span className="text-[10px] font-mono text-surface-400 ml-2">
                        {new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      <p className="text-surface-700 dark:text-surface-300 whitespace-pre-wrap break-words">{m.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MODAL 1: Schedule New Meeting Modal */}
      <Modal
        isOpen={isScheduleModalOpen}
        onClose={() => setIsScheduleModalOpen(false)}
        title="Schedule New Video Meeting"
      >
        <form onSubmit={handleScheduleSubmit} className="space-y-4 text-xs">
          <div>
            <label className="block font-semibold text-surface-700 dark:text-surface-300 mb-1">
              Meeting Title *
            </label>
            <input
              type="text"
              required
              placeholder="e.g. InsureDirect Claims Architecture Sync"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              className="w-full px-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block font-semibold text-surface-700 dark:text-surface-300 mb-1">
                Client / Project
              </label>
              <input
                type="text"
                placeholder="e.g. MedTech Insurance"
                value={newClient}
                onChange={(e) => setNewClient(e.target.value)}
                className="w-full px-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none"
              />
            </div>

            <div>
              <label className="block font-semibold text-surface-700 dark:text-surface-300 mb-1">
                Duration (minutes)
              </label>
              <select
                value={newDuration}
                onChange={(e) => setNewDuration(e.target.value)}
                className="w-full px-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none"
              >
                <option value="15">15 mins (Standup)</option>
                <option value="30">30 mins (Review)</option>
                <option value="45">45 mins (Sprint Sync)</option>
                <option value="60">60 mins (Strategy)</option>
                <option value="90">90 mins (Workshop)</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block font-semibold text-surface-700 dark:text-surface-300 mb-1">
              Date & Time *
            </label>
            <input
              type="datetime-local"
              required
              value={newDateTime}
              onChange={(e) => setNewDateTime(e.target.value)}
              className="w-full px-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none"
            />
          </div>

          {/* Member / Attendee Selection Option */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block font-semibold text-surface-700 dark:text-surface-300">
                Meeting Participants ({selectedAttendeeIds.length} selected) *
              </label>
              <div className="flex items-center space-x-2 text-xs">
                <button
                  type="button"
                  onClick={handleSelectAllAttendees}
                  className="font-bold text-brand-600 hover:text-brand-700 dark:text-brand-400"
                >
                  Select All ({teamMembers.length})
                </button>
                <span className="text-surface-300 dark:text-surface-700">|</span>
                <button
                  type="button"
                  onClick={handleClearAttendees}
                  className="text-surface-500 hover:text-surface-700 dark:hover:text-surface-300"
                >
                  Clear
                </button>
              </div>
            </div>

            {/* Quick Filter */}
            <div className="relative mb-2">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" />
              <input
                type="text"
                placeholder="Search team member to invite..."
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-xs rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none"
              />
            </div>

            {/* Checklist of Workspace Members */}
            <div className="max-h-40 overflow-y-auto space-y-1 p-1 bg-surface-50 dark:bg-surface-850 rounded-2xl border border-surface-200 dark:border-surface-700">
              {filteredScheduleMembers.map((member) => {
                const isSelected = selectedAttendeeIds.includes(member.id);
                return (
                  <div
                    key={member.id}
                    onClick={() => handleToggleAttendee(member.id)}
                    className={`p-2 rounded-xl text-xs flex items-center justify-between cursor-pointer transition-all ${
                      isSelected
                        ? 'bg-brand-500/10 dark:bg-brand-500/20 border border-brand-500/40 text-brand-900 dark:text-brand-200'
                        : 'hover:bg-white dark:hover:bg-surface-800 text-surface-700 dark:text-surface-300 border border-transparent'
                    }`}
                  >
                    <div className="flex items-center space-x-2.5 min-w-0 pr-2">
                      <Avatar name={member.fullName} size="xs" />
                      <div className="min-w-0">
                        <div className="font-bold truncate">{member.fullName}</div>
                        <div className="text-[10px] text-surface-400 truncate">
                          {member.title || member.role} {member.id === user?.id ? '(You / Host)' : ''}
                        </div>
                      </div>
                    </div>
                    <div className="shrink-0">
                      {isSelected ? (
                        <CheckSquare className="w-4 h-4 text-brand-600 dark:text-brand-400" />
                      ) : (
                        <Square className="w-4 h-4 text-surface-400" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* External Guests Section (Schedule Modal) */}
          <div className="pt-3 border-t border-surface-200/60 dark:border-surface-700/60">
            <div className="flex items-center justify-between mb-1">
              <label className="block font-semibold text-surface-700 dark:text-surface-300">
                Invite External Guests (Outside Company)
              </label>
              {externalGuests.length > 0 && (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/30">
                  {externalGuests.length} external guest{externalGuests.length > 1 ? 's' : ''}
                </span>
              )}
            </div>
            <p className="text-[11px] text-surface-500 mb-2">
              Invite clients, contractors, or external partners via their email address.
            </p>
            <div className="flex gap-2">
              <input
                type="email"
                placeholder="guest@external-client.com"
                value={newExternalEmail}
                onChange={(e) => setNewExternalEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddExternalGuest();
                  }
                }}
                className="flex-1 px-3 py-1.5 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 text-xs focus:outline-none"
              />
              <input
                type="text"
                placeholder="Guest Name (optional)"
                value={newExternalName}
                onChange={(e) => setNewExternalName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddExternalGuest();
                  }
                }}
                className="w-1/3 px-3 py-1.5 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 text-xs focus:outline-none"
              />
              <button
                type="button"
                onClick={handleAddExternalGuest}
                className="px-3 py-1.5 rounded-xl bg-surface-900 dark:bg-surface-700 hover:bg-black dark:hover:bg-surface-600 text-white font-bold text-xs"
              >
                + Add
              </button>
            </div>

            {externalGuests.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2.5">
                {externalGuests.map((g) => (
                  <span
                    key={g.email}
                    className="inline-flex items-center space-x-1.5 px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/30 text-[11px] font-semibold"
                  >
                    <span>{g.name || g.email}</span>
                    <span className="text-[10px] text-amber-600/70 font-normal">({g.email})</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveExternalGuest(g.email)}
                      className="hover:text-rose-500 ml-1"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block font-semibold text-surface-700 dark:text-surface-300 mb-1">
              Agenda & Notes
            </label>
            <textarea
              rows={2}
              placeholder="Key discussion topics or preparation notes..."
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              className="w-full px-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none resize-none"
            />
          </div>

          <div className="pt-4 border-t border-surface-100 dark:border-surface-800 flex justify-end space-x-2">
            <button
              type="button"
              onClick={() => setIsScheduleModalOpen(false)}
              className="px-4 py-2 font-medium text-surface-600 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800 rounded-xl"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || (selectedAttendeeIds.length === 0 && externalGuests.length === 0)}
              className="px-5 py-2 font-bold bg-pulse-gradient hover:opacity-95 text-white rounded-xl shadow-pulse transition-all disabled:opacity-50"
            >
              {isSubmitting ? 'Scheduling...' : 'Schedule & Notify'}
            </button>
          </div>
        </form>
      </Modal>

      {/* MODAL 2: Reschedule Meeting Modal */}
      <Modal
        isOpen={Boolean(reschedulingMeeting)}
        onClose={() => setReschedulingMeeting(null)}
        title={`Reschedule Meeting: ${reschedulingMeeting?.title || ''}`}
      >
        <form onSubmit={handleRescheduleSubmit} className="space-y-4 text-xs">
          <div className="p-3 bg-brand-50/50 dark:bg-brand-950/20 rounded-2xl border border-brand-200/50 dark:border-brand-800/40 text-brand-900 dark:text-brand-200">
            <p className="font-medium text-[11px]">
              🔔 Updating this schedule will immediately send real-time notification alerts, sound chimes, and calendar updates to all participants.
            </p>
          </div>

          <div>
            <label className="block font-semibold text-surface-700 dark:text-surface-300 mb-1">
              Meeting Title
            </label>
            <input
              type="text"
              required
              value={rescheduleTitle}
              onChange={(e) => setRescheduleTitle(e.target.value)}
              className="w-full px-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block font-semibold text-surface-700 dark:text-surface-300 mb-1">
                New Date & Time *
              </label>
              <input
                type="datetime-local"
                required
                value={rescheduleDateTime}
                onChange={(e) => setRescheduleDateTime(e.target.value)}
                className="w-full px-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none font-semibold text-brand-600 dark:text-brand-400"
              />
            </div>

            <div>
              <label className="block font-semibold text-surface-700 dark:text-surface-300 mb-1">
                Duration (minutes)
              </label>
              <select
                value={rescheduleDuration}
                onChange={(e) => setRescheduleDuration(e.target.value)}
                className="w-full px-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none"
              >
                <option value="15">15 mins (Standup)</option>
                <option value="30">30 mins (Review)</option>
                <option value="45">45 mins (Sprint Sync)</option>
                <option value="60">60 mins (Strategy)</option>
                <option value="90">90 mins (Workshop)</option>
              </select>
            </div>
          </div>

          {/* Reschedule Reason */}
          <div>
            <label className="block font-semibold text-surface-700 dark:text-surface-300 mb-1">
              Reschedule Reason (Optional note for participants)
            </label>
            <input
              type="text"
              placeholder="e.g. Moved to accommodate client review, shifted forward 1 hour"
              value={rescheduleReason}
              onChange={(e) => setRescheduleReason(e.target.value)}
              className="w-full px-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none"
            />
          </div>

          {/* Manage Participants */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block font-semibold text-surface-700 dark:text-surface-300">
                Manage Participants ({rescheduleAttendees.length} selected)
              </label>
              <div className="flex items-center space-x-2 text-xs">
                <button
                  type="button"
                  onClick={handleSelectAllRescheduleAttendees}
                  className="font-bold text-brand-600 hover:text-brand-700 dark:text-brand-400"
                >
                  Select All ({teamMembers.length})
                </button>
                <span className="text-surface-300 dark:text-surface-700">|</span>
                <button
                  type="button"
                  onClick={handleClearRescheduleAttendees}
                  className="text-surface-500 hover:text-surface-700 dark:hover:text-surface-300"
                >
                  Clear
                </button>
              </div>
            </div>

            {/* Filter Search */}
            <div className="relative mb-2">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" />
              <input
                type="text"
                placeholder="Search member..."
                value={rescheduleMemberSearch}
                onChange={(e) => setRescheduleMemberSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-xs rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none"
              />
            </div>

            {/* Checklist */}
            <div className="max-h-36 overflow-y-auto space-y-1 p-1 bg-surface-50 dark:bg-surface-850 rounded-2xl border border-surface-200 dark:border-surface-700">
              {filteredRescheduleMembers.map((member) => {
                const isSelected = rescheduleAttendees.includes(member.id);
                return (
                  <div
                    key={member.id}
                    onClick={() => handleToggleRescheduleAttendee(member.id)}
                    className={`p-2 rounded-xl text-xs flex items-center justify-between cursor-pointer transition-all ${
                      isSelected
                        ? 'bg-brand-500/10 dark:bg-brand-500/20 border border-brand-500/40 text-brand-900 dark:text-brand-200'
                        : 'hover:bg-white dark:hover:bg-surface-800 text-surface-700 dark:text-surface-300 border border-transparent'
                    }`}
                  >
                    <div className="flex items-center space-x-2.5 min-w-0 pr-2">
                      <Avatar name={member.fullName} size="xs" />
                      <div className="min-w-0">
                        <div className="font-bold truncate">{member.fullName}</div>
                        <div className="text-[10px] text-surface-400 truncate">
                          {member.title || member.role} {member.id === user?.id ? '(You)' : ''}
                        </div>
                      </div>
                    </div>
                    <div className="shrink-0">
                      {isSelected ? (
                        <CheckSquare className="w-4 h-4 text-brand-600 dark:text-brand-400" />
                      ) : (
                        <Square className="w-4 h-4 text-surface-400" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* External Guests Section (Reschedule Modal) */}
          <div className="pt-3 border-t border-surface-200/60 dark:border-surface-700/60">
            <div className="flex items-center justify-between mb-1">
              <label className="block font-semibold text-surface-700 dark:text-surface-300">
                Manage External Guests (Outside Company)
              </label>
              {rescheduleExternalGuests.length > 0 && (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/30">
                  {rescheduleExternalGuests.length} external guest{rescheduleExternalGuests.length > 1 ? 's' : ''}
                </span>
              )}
            </div>
            <p className="text-[11px] text-surface-500 mb-2">
              Invite clients, contractors, or external partners via their email address.
            </p>
            <div className="flex gap-2">
              <input
                type="email"
                placeholder="guest@external-client.com"
                value={rescheduleNewExternalEmail}
                onChange={(e) => setRescheduleNewExternalEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddRescheduleExternalGuest();
                  }
                }}
                className="flex-1 px-3 py-1.5 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 text-xs focus:outline-none"
              />
              <input
                type="text"
                placeholder="Guest Name (optional)"
                value={rescheduleNewExternalName}
                onChange={(e) => setRescheduleNewExternalName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddRescheduleExternalGuest();
                  }
                }}
                className="w-1/3 px-3 py-1.5 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 text-xs focus:outline-none"
              />
              <button
                type="button"
                onClick={handleAddRescheduleExternalGuest}
                className="px-3 py-1.5 rounded-xl bg-surface-900 dark:bg-surface-700 hover:bg-black dark:hover:bg-surface-600 text-white font-bold text-xs"
              >
                + Add
              </button>
            </div>

            {rescheduleExternalGuests.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2.5">
                {rescheduleExternalGuests.map((g) => (
                  <span
                    key={g.email}
                    className="inline-flex items-center space-x-1.5 px-2.5 py-1 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/30 text-[11px] font-semibold"
                  >
                    <span>{g.name || g.email}</span>
                    <span className="text-[10px] text-amber-600/70 font-normal">({g.email})</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveRescheduleExternalGuest(g.email)}
                      className="hover:text-rose-500 ml-1"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="pt-4 border-t border-surface-100 dark:border-surface-800 flex justify-end space-x-2">
            <button
              type="button"
              onClick={() => setReschedulingMeeting(null)}
              className="px-4 py-2 font-medium text-surface-600 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800 rounded-xl"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isRescheduling || (rescheduleAttendees.length === 0 && rescheduleExternalGuests.length === 0)}
              className="px-5 py-2 font-bold bg-pulse-gradient hover:opacity-95 text-white rounded-xl shadow-pulse transition-all disabled:opacity-50"
            >
              {isRescheduling ? 'Rescheduling...' : 'Confirm & Notify All'}
            </button>
          </div>
        </form>
      </Modal>

      {/* MODAL 3: Cancel Confirmation Modal */}
      <Modal
        isOpen={Boolean(cancellingMeeting)}
        onClose={() => setCancellingMeeting(null)}
        title="Cancel Meeting"
      >
        <form onSubmit={handleCancelSubmit} className="space-y-4 text-xs">
          <div className="p-4 bg-rose-50 dark:bg-rose-950/30 rounded-2xl border border-rose-200 dark:border-rose-900/50 flex space-x-3">
            <AlertCircle className="w-5 h-5 text-rose-600 dark:text-rose-400 shrink-0 mt-0.5" />
            <div className="space-y-1">
              <h4 className="font-bold text-rose-900 dark:text-rose-200">
                Cancel "{cancellingMeeting?.title}"?
              </h4>
              <p className="text-rose-700 dark:text-rose-300 leading-relaxed text-[11px]">
                All {cancellingMeeting?.attendees?.length || 0} participants will be notified immediately through real-time notifications, audio chimes, and browser push alerts.
              </p>
            </div>
          </div>

          <div>
            <label className="block font-semibold text-surface-700 dark:text-surface-300 mb-1">
              Reason for Cancellation (Optional)
            </label>
            <input
              type="text"
              placeholder="e.g. Client postponed delivery, topic resolved in asynchronous chat"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              className="w-full px-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none"
            />
          </div>

          <div className="pt-4 border-t border-surface-100 dark:border-surface-800 flex justify-end space-x-2">
            <button
              type="button"
              onClick={() => setCancellingMeeting(null)}
              className="px-4 py-2 font-medium text-surface-600 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800 rounded-xl"
            >
              Keep Meeting
            </button>
            <button
              type="submit"
              disabled={isCancelling}
              className="px-5 py-2 font-bold bg-rose-600 hover:bg-rose-700 text-white rounded-xl shadow-sm transition-all"
            >
              {isCancelling ? 'Cancelling...' : 'Confirm Cancellation'}
            </button>
          </div>
        </form>
      </Modal>

      {/* MODAL 4: Instant Meeting Share Link & Attendees Modal */}
      <Modal
        isOpen={instantMeetingModalOpen}
        onClose={() => setInstantMeetingModalOpen(false)}
        title="Instant Meeting Room"
      >
        <div className="space-y-4 text-xs">
          <div className="p-4 bg-brand-50 dark:bg-brand-950/30 rounded-2xl border border-brand-200 dark:border-brand-900/50 flex items-start space-x-3">
            <Radio className="w-5 h-5 text-brand-600 dark:text-brand-400 shrink-0 mt-0.5 animate-pulse" />
            <div className="space-y-1">
              <h4 className="font-bold text-brand-900 dark:text-brand-200">
                {instantMeeting?.title || 'Instant Team Sync'}
              </h4>
              <p className="text-brand-700 dark:text-brand-300 leading-relaxed text-[11px]">
                Your live WebRTC conference room is active. Share the direct invitation link below or select team members to notify them immediately.
              </p>
            </div>
          </div>

          {/* Shareable Link Box */}
          <div className="space-y-1.5">
            <label className="block font-semibold text-surface-700 dark:text-surface-300">
              Meeting Invitation Link
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                readOnly
                value={
                  instantMeeting
                    ? `${window.location.origin}/?meeting=${instantMeeting.id}`
                    : ''
                }
                className="flex-1 px-3 py-2 text-xs rounded-xl bg-surface-100 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 font-mono select-all focus:outline-none"
              />
              <button
                type="button"
                onClick={handleCopyInstantLink}
                className="px-3.5 py-2 rounded-xl bg-surface-200 dark:bg-surface-700 hover:bg-surface-300 text-surface-800 dark:text-surface-200 font-bold flex items-center space-x-1.5 shrink-0 transition-colors"
              >
                {copiedInstantLink ? (
                  <>
                    <Check className="w-4 h-4 text-emerald-500" />
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    <span>Copy Link</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Add / Invite Team Members */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="font-semibold text-surface-700 dark:text-surface-300">
                Invite Team Members ({instantAttendeeIds.length} selected)
              </label>
              <button
                type="button"
                onClick={handleAddInstantAttendees}
                disabled={isSavingInstantAttendees || instantAttendeeIds.length === 0}
                className="text-[11px] font-bold text-brand-600 dark:text-brand-400 hover:underline disabled:opacity-50"
              >
                {isSavingInstantAttendees ? 'Inviting...' : 'Send Invitations'}
              </button>
            </div>

            <div className="max-h-48 overflow-y-auto space-y-1.5 pr-1 border border-surface-200 dark:border-surface-700 rounded-xl p-2 bg-surface-50/50 dark:bg-surface-900/50">
              {teamMembers.map((member) => {
                const isSelected = instantAttendeeIds.includes(member.id);
                return (
                  <button
                    key={member.id}
                    type="button"
                    onClick={() => handleToggleInstantAttendee(member.id)}
                    className={`w-full flex items-center justify-between p-2 rounded-lg text-left transition-colors ${
                      isSelected
                        ? 'bg-brand-50 dark:bg-brand-950/40 text-brand-700 dark:text-brand-300'
                        : 'hover:bg-surface-100 dark:hover:bg-surface-800 text-surface-700 dark:text-surface-300'
                    }`}
                  >
                    <div className="flex items-center space-x-2 min-w-0">
                      <Avatar src={member.avatarUrl} name={member.fullName} size="xs" />
                      <div className="min-w-0">
                        <div className="font-medium truncate text-xs">{member.fullName}</div>
                        <div className="text-[10px] text-surface-400 truncate">{member.email}</div>
                      </div>
                    </div>
                    {isSelected ? (
                      <CheckCircle2 className="w-4 h-4 text-brand-600 dark:text-brand-400 shrink-0" />
                    ) : (
                      <div className="w-4 h-4 rounded-full border border-surface-300 dark:border-surface-600 shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Modal Actions */}
          <div className="pt-4 border-t border-surface-100 dark:border-surface-800 flex justify-end space-x-2">
            <button
              type="button"
              onClick={() => setInstantMeetingModalOpen(false)}
              className="px-4 py-2 font-medium text-surface-600 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800 rounded-xl"
            >
              Close
            </button>
            <button
              type="button"
              onClick={async () => {
                setInstantMeetingModalOpen(false);
                if (instantMeeting) {
                  const channelId = await getMeetingChannelId(instantMeeting.id);
                  startGroupCall({
                    id: channelId || instantMeeting.id,
                    name: instantMeeting.title,
                  });
                }
              }}
              className="px-5 py-2 font-bold bg-pulse-gradient hover:opacity-95 text-white rounded-xl shadow-pulse transition-all flex items-center space-x-1.5"
            >
              <Play className="w-3.5 h-3.5 fill-white" />
              <span>Join Call Now</span>
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
